/**
 * Виджет «Налоговые поступления юридических лиц» (US-04.3 FR-3a, источник —
 * форма 2-47 системы Еакимат): две диаграммы.
 *
 * 1. «Топ-10 компаний по налоговым поступлениям» — таблица (строка =
 *    компания: название · БИН · ОКЭД · сумма платежа), сортировка по
 *    убыванию; клик по строке раскрывает детализацию платежей (вид налога
 *    (КБК) · сумма · дата оплаты) — FR-3a.3.
 * 2. «Налоговые поступления компаний по видам их деятельности» — pie-chart
 *    по разделам ОКЭД (FR-3a.4); клик по доле/легенде — drill-through:
 *    таблица ВСЕХ компаний раздела с той же детализацией (FR-3a.5).
 *
 * Источник данных — гибрид:
 *  - демо-КАТО → проп `demo` (мок hardData): топ-10, разрез по ОКЭД и
 *    платежи считаются на фронте из списка компаний;
 *  - остальные КАТО → ресты `/analytics/legal-entity-taxes*` (см.
 *    analytics.request.ts): основной эндпоинт грузится при открытии,
 *    платежи компании и drill-through раздела — по клику, не заранее.
 *    Территория адресуется параметром kato (бэк резолвит его в
 *    top_code_region сам); КАТО без соответствия (404) или
 *    `total === null` → карточки с подписью «Нет данных»
 *    (замечание ревью: виджет не скрываем).
 *
 * Суммы API — тенге; на фронте пересчитываются в млн тг (млрд — когда итог
 * от 10 млрд) с явной подписью единицы (FR-3a.6). «Отчётный период» —
 * из `period` ответа, календарные даты не передаются.
 */
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';

import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import { useKato } from '../../../contexts/KatoContext';
import { cn } from '../../../lib/cn';
import {
  getLegalEntityTaxes,
  getLegalTaxesByActivity,
  getLegalTaxesPayments,
} from '../../../requests/analytics.request';
import type {
  LegalTaxesCompany,
  LegalTaxesResponse,
} from '../../../types/analytics';

const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/** «2026-08-17» → «17.08.2026» (даты периода и платежей). */
const fmtDate = (iso: string | null): string | null => {
  if (iso === null) return null;
  const [year, month, day] = iso.split('-');
  return year && month && day ? `${day}.${month}.${year}` : iso;
};

export interface LegalTaxPaymentVM {
  taxType: string | null;
  amount: number | null;
  period: string | null;
}

export interface LegalTaxCompanyVM {
  name: string;
  bin: string | null;
  oked: string | null;
  /** Итог по компании = сумма всех её налоговых платежей (FR-3a.2). */
  total: number | null;
  payments: LegalTaxPaymentVM[];
}

interface LegalEntityTaxesProps {
  /** Демо-мок формы 2-47 (unit + компании с платежами); null — ресты. */
  demo: { unit: string | null; companies: LegalTaxCompanyVM[] } | null;
}

/** Палитра долей ОКЭД — как pie-чарты соседних секций. */
const OKED_COLORS = ['#4A63E7', '#2C9C42', '#D89B1F', '#8FA3F2', '#A620F0', '#E87BA4'];

/** Разделов ОКЭД в pie до «Прочих» — по числу цветов минус «Прочие». */
const PIE_MAIN_SECTIONS = OKED_COLORS.length - 1;

/** Кэш ленивых платежей по БИН: 'loading' — запрос в полёте. */
type PaymentsByBin = Record<string, LegalTaxPaymentVM[] | 'loading'>;

/** Таблица компаний с раскрытием платежей (общая для топ-10 и drill-through). */
const CompaniesTable = ({
  companies,
  unit,
  testId,
  paymentsByBin,
  onExpandCompany,
}: {
  companies: LegalTaxCompanyVM[];
  unit: string | null;
  testId: string;
  /** API-режим: платежи по клику; в демо приходят внутри компании. */
  paymentsByBin?: PaymentsByBin;
  onExpandCompany?: (company: LegalTaxCompanyVM) => void;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const keyOf = (company: LegalTaxCompanyVM): string =>
    company.bin ?? company.name;

  const toggle = (company: LegalTaxCompanyVM) => {
    const key = keyOf(company);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    if (!expanded.has(key)) {
      onExpandCompany?.(company);
    }
  };

  /** Платежи строки: из компании (демо) или из кэша по БИН (API). */
  const paymentsOf = (
    company: LegalTaxCompanyVM,
  ): LegalTaxPaymentVM[] | 'loading' =>
    company.payments.length > 0
      ? company.payments
      : (company.bin !== null ? (paymentsByBin?.[company.bin] ?? []) : []);

  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <div className="min-w-[30rem]">
        <div className="flex items-center gap-3 px-2 pb-2 text-xs uppercase leading-tight tracking-wide text-text-faint">
          <span className="w-4 shrink-0" />
          <span className="min-w-0 flex-1 sm:flex-none sm:basis-1/3">
            {t('analytics.economy.legalTaxes.columnCompany')}
          </span>
          <span className="w-28 shrink-0">
            {t('analytics.economy.legalTaxes.columnBin')}
          </span>
          <span className="hidden min-w-0 flex-1 sm:block">
            {t('analytics.economy.legalTaxes.columnOked')}
          </span>
          {/* «Сумма платежа,» и единица — двумя строками (замечание
              ревью), правый край совпадает с колонкой значений. */}
          <span className="w-28 shrink-0 text-right">
            <span className="block whitespace-nowrap">
              {t('analytics.economy.legalTaxes.columnAmount')}
              {unit && ','}
            </span>
            {unit && <span className="block">{unit}</span>}
          </span>
        </div>
        <ul>
          {companies.map((company) => {
            const key = keyOf(company);
            const isOpen = expanded.has(key);
            const payments = isOpen ? paymentsOf(company) : [];
            return (
              <li
                key={key}
                className="border-b border-surface-border last:border-b-0"
              >
                {/* FR-3a.3: клик по строке раскрывает платежи компании. */}
                <button
                  type="button"
                  onClick={() => toggle(company)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-3 px-2 py-3 text-left text-sm hover:bg-brand-50/60"
                  data-testid={`${testId}-row-${key}`}
                >
                  <ChevronRight
                    className={cn(
                      'size-4 shrink-0 text-text-faint transition-transform',
                      isOpen && 'rotate-90',
                    )}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 font-semibold leading-snug sm:flex-none sm:basis-1/3">
                    {company.name}
                  </span>
                  <span className="w-28 shrink-0 tabular-nums text-text-muted">
                    {company.bin ?? '—'}
                  </span>
                  {/* ОКЭД — полное наименование без обрезки (замечание
                      ревью): колонка тянется, текст переносится. */}
                  <span className="hidden min-w-0 flex-1 leading-snug text-text-muted sm:block">
                    {company.oked ?? '—'}
                  </span>
                  <span className="w-24 shrink-0 text-right font-extrabold tabular-nums text-chart-indigo">
                    {company.total !== null ? fmt1(company.total) : '—'}
                  </span>
                </button>
                {isOpen && payments === 'loading' && (
                  <p className="px-9 pb-3 text-sm lowercase text-text-muted">
                    {t('common.loading')}
                  </p>
                )}
                {isOpen && payments !== 'loading' && payments.length > 0 && (
                  <ul
                    className="bg-surface-page/70 px-2 py-1"
                    data-testid={`${testId}-payments-${key}`}
                  >
                    <li className="flex items-center gap-3 py-1.5 text-xs uppercase tracking-wide text-text-faint">
                      <span className="w-4 shrink-0" />
                      <span className="min-w-0 flex-1">
                        {t('analytics.economy.legalTaxes.columnTaxType')}
                      </span>
                      <span className="w-36 shrink-0">
                        {t('analytics.economy.legalTaxes.columnPeriod')}
                      </span>
                      <span className="w-24 shrink-0 text-right">
                        {t('analytics.economy.legalTaxes.columnSum')}
                        {unit && `, ${unit}`}
                      </span>
                    </li>
                    {payments.map((payment, index) => (
                      <li
                        key={`${payment.taxType}-${index}`}
                        className="flex items-center gap-3 border-t border-dashed border-surface-border py-2 text-sm"
                      >
                        <span className="w-4 shrink-0" />
                        <span className="min-w-0 flex-1 text-text-muted">
                          {payment.taxType ?? '—'}
                        </span>
                        <span className="w-36 shrink-0 text-text-muted">
                          {payment.period ?? '—'}
                        </span>
                        <span className="w-24 shrink-0 text-right font-bold tabular-nums">
                          {payment.amount !== null
                            ? fmt1(payment.amount)
                            : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

/** Доля pie: label раздела, value уже в единицах виджета. */
interface SectionSegmentVM {
  key: string;
  label: string;
  value: number;
  share: number | null;
  color: string;
  /** «Прочие виды деятельности» — собраны на фронте, drill-through нет. */
  synthetic: boolean;
}

export const LegalEntityTaxes = ({ demo }: LegalEntityTaxesProps) => {
  const { t } = useTranslation();
  const { selectedKato } = useKato();
  const isDemo = demo !== null;

  const [apiData, setApiData] = useState<LegalTaxesResponse | null>(null);
  const [loading, setLoading] = useState(!isDemo);
  const [paymentsByBin, setPaymentsByBin] = useState<PaymentsByBin>({});
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  /** API-режим: компании drill-through раздела ('loading' — в полёте). */
  const [drillLoaded, setDrillLoaded] = useState<
    LegalTaxCompanyVM[] | 'loading'
  >('loading');

  // Основной эндпоинт — при открытии; drill/платежи — по клику (тикет).
  useEffect(() => {
    if (isDemo) return undefined;
    let cancelled = false;
    setApiData(null);
    setPaymentsByBin({});
    setSelectedSection(null);
    if (selectedKato === null) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getLegalEntityTaxes(selectedKato)
      .then((response) => {
        if (!cancelled) setApiData(response);
      })
      // 404 (период не резолвится) = данных нет → виджет скрыт.
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isDemo, selectedKato]);

  // Единица сумм: API — тенге, показываем в млн тг (млрд — от 10 млрд
  // итога, уровень области); демо-мок несёт единицу сам.
  const divisor =
    apiData?.total !== null && apiData !== undefined && apiData !== null
      ? (apiData.total >= 1e10 ? 1e9 : 1e6)
      : 1e6;
  const unit = isDemo
    ? demo.unit
    : t(divisor === 1e9 ? 'units.blnTenge' : 'units.mlnTenge');

  const companyVM = (company: LegalTaxesCompany): LegalTaxCompanyVM => ({
    name: company.name,
    bin: company.bin,
    // В таблице — наименование ОКЭД (читаемое), фолбэк — код.
    oked: company.oked_name ?? company.oked,
    total: company.amount !== null ? company.amount / divisor : null,
    payments: [],
  });

  // --- Топ-10 (FR-3a.3): демо — считаем на фронте, API отдаёт готовым ---
  const top10: LegalTaxCompanyVM[] = isDemo
    ? [...demo.companies]
        .sort((a, b) => (b.total ?? 0) - (a.total ?? 0))
        .slice(0, 10)
    : (apiData?.top ?? []).map(companyVM);

  // --- Разрез по видам деятельности (FR-3a.4) ---
  let segments: SectionSegmentVM[];
  if (isDemo) {
    // Демо: группировка компаний по наименованию ОКЭД.
    const byOked = new Map<string, number>();
    for (const company of demo.companies) {
      const oked = company.oked ?? '—';
      byOked.set(oked, (byOked.get(oked) ?? 0) + (company.total ?? 0));
    }
    const demoTotal = [...byOked.values()].reduce((acc, v) => acc + v, 0);
    segments = [...byOked.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([oked, total], index) => ({
        key: oked,
        label: oked,
        value: total,
        share: demoTotal > 0 ? (total / demoTotal) * 100 : null,
        color: OKED_COLORS[index % OKED_COLORS.length],
        synthetic: false,
      }));
  } else {
    // API: все разделы ОКЭД; хвост за пределами палитры — «Прочие»
    // (drill-through по ним недоступен — раздел синтетический).
    //
    // Защита от аномалий данных (СКО-область): разделы с не-положительной
    // суммой (сторно/возвраты перевешивают поступления) в pie и «Прочие»
    // не попадают — донат не умеет рисовать минус. Доли пересчитываются
    // от суммы положительных разделов: share ответа при таких данных
    // бессмыслен (тысячи процентов от почти нулевого total).
    const activities = [...(apiData?.by_activity ?? [])]
      .filter(
        (activity): activity is { section: string; amount: number; share: number | null } =>
          activity.amount !== null && activity.amount > 0,
      )
      .sort((a, b) => b.amount - a.amount);
    const positiveSum = activities.reduce(
      (acc, activity) => acc + activity.amount,
      0,
    );
    const shareOf = (amount: number): number | null =>
      positiveSum > 0 ? (amount / positiveSum) * 100 : null;
    const head = activities.slice(0, PIE_MAIN_SECTIONS);
    const tail = activities.slice(PIE_MAIN_SECTIONS);
    segments = head.map((activity, index) => ({
      key: activity.section,
      label: activity.section,
      value: activity.amount / divisor,
      share: shareOf(activity.amount),
      color: OKED_COLORS[index],
      synthetic: false,
    }));
    if (tail.length > 0) {
      const tailSum = tail.reduce(
        (acc, activity) => acc + activity.amount,
        0,
      );
      segments.push({
        key: '__others__',
        label: t('analytics.economy.legalTaxes.othersSection'),
        value: tailSum / divisor,
        share: shareOf(tailSum),
        color: OKED_COLORS[OKED_COLORS.length - 1],
        synthetic: true,
      });
    }
  }

  // --- Drill-through (FR-3a.5): демо — фильтр мока, API — эндпоинт ---
  const toggleSection = (segment: SectionSegmentVM) => {
    if (segment.synthetic) return;
    if (selectedSection === segment.key) {
      setSelectedSection(null);
      return;
    }
    setSelectedSection(segment.key);
    if (isDemo || selectedKato === null) return;
    setDrillLoaded('loading');
    getLegalTaxesByActivity(selectedKato, segment.key)
      .then((response) => {
        setDrillLoaded((response?.items ?? []).map(companyVM));
      })
      .catch(() => setDrillLoaded([]));
  };

  const drillCompanies: LegalTaxCompanyVM[] | 'loading' =
    selectedSection === null
      ? []
      : isDemo
        ? demo.companies
            .filter((company) => (company.oked ?? '—') === selectedSection)
            .sort((a, b) => (b.total ?? 0) - (a.total ?? 0))
        : drillLoaded;

  // Платежи компании — по клику на строку, с кэшем по БИН (API-режим).
  const loadPayments = (company: LegalTaxCompanyVM) => {
    const bin = company.bin;
    if (isDemo || bin === null || selectedKato === null) return;
    if (paymentsByBin[bin] !== undefined) return;
    setPaymentsByBin((prev) => ({ ...prev, [bin]: 'loading' }));
    getLegalTaxesPayments(selectedKato, bin)
      .then((response) => {
        setPaymentsByBin((prev) => ({
          ...prev,
          [bin]: (response?.items ?? []).map((payment) => ({
            taxType: payment.kbk_name ?? payment.kbk,
            amount:
              payment.amount !== null ? payment.amount / divisor : null,
            period: fmtDate(payment.paid_at),
          })),
        }));
      })
      .catch(() => {
        setPaymentsByBin((prev) => ({ ...prev, [bin]: [] }));
      });
  };

  const chartConfig = Object.fromEntries(
    segments.map((segment) => [
      segment.key,
      { label: segment.label, color: segment.color },
    ]),
  ) satisfies ChartConfig;

  const period = apiData?.period ?? null;
  const periodLabel =
    period && (period.date_from !== null || period.date_to !== null)
      ? t('analytics.economy.legalTaxes.period', {
          from: fmtDate(period.date_from) ?? '…',
          to: fmtDate(period.date_to) ?? '…',
        })
      : null;

  return (
    <>
      <div className="flex items-center gap-4 pt-2">
        <h3 className="shrink-0 text-lg font-extrabold">
          {t('analytics.economy.legalTaxes.title')}
        </h3>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        {/* «Отчётный период» — фактический из ответа (не календарь). */}
        {periodLabel !== null && (
          <span
            className="shrink-0 text-xs text-text-faint"
            data-testid="analytics-legal-taxes-period"
          >
            {periodLabel}
          </span>
        )}
      </div>

      {/* Карточки — друг под другом на всю ширину: у таблицы топ-10 и
          легенды длинные названия, в два столбца они не читаются
          (замечание ревью). */}
      {!isDemo && loading ? (
        <BlockLoader />
      ) : (
      <div
        className="grid gap-3.5"
        data-testid="analytics-legal-taxes"
      >
        {/* Диаграмма 1: топ-10 компаний (FR-3a.3). */}
        <Card className="shadow-card">
          <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-2 space-y-0">
            <CardTitle>
              {t('analytics.economy.legalTaxes.topTitle')}
            </CardTitle>
            <span className="shrink-0 text-xs text-text-faint">
              {t('analytics.economy.legalTaxes.rowHint')}
            </span>
          </CardHeader>
          <CardContent>
            {top10.length === 0 ? (
              <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
            ) : (
              <CompaniesTable
                companies={top10}
                unit={unit}
                testId="analytics-legal-taxes-top"
                paymentsByBin={isDemo ? undefined : paymentsByBin}
                onExpandCompany={isDemo ? undefined : loadPayments}
              />
            )}
          </CardContent>
        </Card>

        {/* Диаграмма 2: pie по разделам ОКЭД + drill-through (FR-3a.4/3a.5). */}
        <Card className="shadow-card">
          <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-2 space-y-0">
            <CardTitle>
              {t('analytics.economy.legalTaxes.byOkedTitle')}
            </CardTitle>
            <span className="shrink-0 text-xs text-text-faint">
              {t('analytics.economy.legalTaxes.pieHint')}
            </span>
          </CardHeader>
          <CardContent>
            {segments.length === 0 ? (
              <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
            ) : (
              <>
                {/* Pie и легенда — в один ряд (замечание ревью): донат
                    слева, разделы с суммами и долями — колонкой справа. */}
                <div className="flex flex-wrap items-center gap-6">
                <div className="w-full max-w-[260px] shrink-0">
                  <ChartContainer config={chartConfig} className="aspect-square">
                    <PieChart>
                      <ChartTooltip
                        cursor={false}
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Pie
                        data={segments.map((segment) => ({
                          key: segment.key,
                          value: segment.value,
                        }))}
                        dataKey="value"
                        nameKey="key"
                        innerRadius="55%"
                        outerRadius="98%"
                        strokeWidth={2}
                        onClick={(_, index) => {
                          const segment = segments[index];
                          if (segment) toggleSection(segment);
                        }}
                      >
                        {segments.map((segment) => (
                          <Cell
                            key={segment.key}
                            fill={segment.color}
                            cursor={segment.synthetic ? undefined : 'pointer'}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                </div>

                <ul className="min-w-64 flex-1 space-y-1.5">
                  {segments.map((segment) => (
                    <li key={segment.key}>
                      <button
                        type="button"
                        disabled={segment.synthetic}
                        onClick={() => toggleSection(segment)}
                        aria-pressed={selectedSection === segment.key}
                        className={cn(
                          'flex w-full items-baseline gap-2 rounded-md px-1.5 py-1 text-left text-sm',
                          !segment.synthetic && 'hover:bg-brand-50/60',
                          selectedSection === segment.key && 'bg-brand-50',
                        )}
                        data-testid={`analytics-legal-taxes-oked-${segment.key}`}
                      >
                        <span
                          className="size-2.5 shrink-0 self-center rounded-sm"
                          style={{ backgroundColor: segment.color }}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1 leading-snug text-text-muted">
                          {segment.label}
                        </span>
                        <span className="whitespace-nowrap font-bold tabular-nums">
                          {fmt1(segment.value)}
                          {unit && (
                            <span className="ml-1 font-normal text-text-muted">
                              {unit}
                            </span>
                          )}
                        </span>
                        {segment.share !== null && (
                          <span className="w-12 shrink-0 text-right text-xs tabular-nums text-text-faint">
                            {fmt1(segment.share)}%
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
                </div>

                {/* FR-3a.5: все компании выбранного раздела (не топ-10). */}
                {selectedSection !== null && (
                  <div className="mt-4 border-t border-surface-border pt-3">
                    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                      <h4 className="text-sm font-extrabold leading-snug">
                        {t('analytics.economy.legalTaxes.drillTitle', {
                          oked: selectedSection,
                        })}
                      </h4>
                      <button
                        type="button"
                        onClick={() => setSelectedSection(null)}
                        className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                        data-testid="analytics-legal-taxes-drill-close"
                      >
                        {t('analytics.economy.legalTaxes.drillClose')}
                      </button>
                    </div>
                    {drillCompanies === 'loading' ? (
                      <BlockLoader />
                    ) : (
                      drillCompanies.length > 0 && (
                        <CompaniesTable
                          companies={drillCompanies}
                          unit={unit}
                          testId="analytics-legal-taxes-drill"
                          paymentsByBin={isDemo ? undefined : paymentsByBin}
                          onExpandCompany={isDemo ? undefined : loadPayments}
                        />
                      )
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
      )}
    </>
  );
};
